import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, CheckSquare, Square, StickyNote, Loader2, CalendarClock, Trash2, ChevronDown, Paperclip, Upload, Eye, FileText, FileImage, File as FileIcon, X, ArrowLeft, MessageSquare, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { StatusBadge } from "./StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "./ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "./ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDate, formatDateTime } from "../lib/utils-format";

const TASK_TYPES = ["Call", "Email", "SMS", "Demo", "Follow-Up", "Research", "Proposal", "General"];
const TASK_PRIORITIES = ["Low", "Medium", "High"];
const TASK_STATUSES = ["To Do", "In Progress", "Completed", "Cancelled"];
const BLANK_TASK = { title: "", description: "", task_type: "Follow-Up", priority: "Medium", due_date: "", status: "To Do" };

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fileIcon = (ct = "") => (ct.startsWith("image/") ? FileImage : ct === "application/pdf" ? FileText : FileIcon);

// Attachments for a single task. Files are stored in the shared project_documents
// library (same store as the Documents page & the record's Documents card) with a
// task_id link, so a will-serve letter attached here also shows in Documents.
export function TaskAttachments({ taskId, recordId, resource, onChange, task, onAutoComplete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/documents?task_id=${taskId}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", recordId);
      fd.append("task_id", taskId);
      fd.append("category", "Due Diligence");
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Attaching evidence to a due-diligence task auto-completes it.
      if (task?.source === "angela_due_diligence" && task?.status !== "Completed" && onAutoComplete) {
        await onAutoComplete(taskId);
        toast.success("Evidence attached — task marked complete");
      } else {
        toast.success("Attachment uploaded");
      }
      load(); onChange && onChange();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const preview = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.content_type || res.data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (doc) => {
    try { await api.delete(`/documents/${doc.id}`); toast.success("Attachment removed"); load(); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-3 rounded-md border border-border/60 bg-secondary/20 p-3" data-testid={`task-attachments-${taskId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5" />Attachments{items.length ? ` (${items.length})` : ""}
        </div>
        <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid={`task-attach-file-${taskId}`} />
        <Button size="sm" variant="outline" className="h-7 px-2.5" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`task-attach-upload-${taskId}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{busy ? "Uploading…" : "Attach"}
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-1">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground py-1" data-testid={`task-attach-empty-${taskId}`}>Upload the will-serve letter, agency reply, or any evidence. It also appears in Documents.</div>
      ) : (
        <ul className="space-y-1">
          {items.map((d) => {
            const Icon = fileIcon(d.content_type);
            return (
              <li key={d.id} className="flex items-center gap-2 text-xs" data-testid={`task-attach-row-${d.id}`}>
                <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="font-medium truncate flex-1">{d.original_filename}</span>
                {d.size ? <span className="text-muted-foreground shrink-0">{fmtSize(d.size)}</span> : null}
                <button onClick={() => preview(d)} className="p-1 rounded hover:bg-accent shrink-0" title="Open" data-testid={`task-attach-preview-${d.id}`}><Eye className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(d)} className="p-1 rounded hover:bg-accent text-destructive shrink-0" title="Remove" data-testid={`task-attach-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Per-task interaction log. Stored as task-scoped notes (task_id, no record_id) so
// they stay on the task and never pollute the record-level Notes card / count.
export function TaskInteractions({ taskId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/notes?task_id=${taskId}&limit=100&sort_by=created_at&sort_dir=desc`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/notes", { body: text.trim(), task_id: taskId, author_name: user?.name });
      setText(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/notes/${id}?hard=true`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); add(); }
  };

  return (
    <div className="rounded-md border border-border/60 bg-secondary/20 p-4" data-testid={`task-interactions-${taskId}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        <MessageSquare className="h-3.5 w-3.5" />Interactions{items.length ? ` (${items.length})` : ""}
      </div>
      <div className="flex items-start gap-2 mb-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Log a call, email, or update… (⌘/Ctrl+Enter to save)"
          className="min-h-[64px] resize-y"
          data-testid="task-interaction-input"
        />
        <Button onClick={add} disabled={busy || !text.trim()} className="shrink-0" data-testid="task-interaction-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="ml-1.5 hidden sm:inline">Log</span>
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-1">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground py-1" data-testid="task-interactions-empty">No interactions logged yet. Track calls, emails, and updates here instead of in the description.</div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it) => (
            <li key={it.id} className="flex items-start gap-2.5 group" data-testid={`task-interaction-row-${it.id}`}>
              <div className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm whitespace-pre-wrap break-words text-foreground">{it.body}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{it.author_name || "Someone"} · {formatDateTime(it.created_at)}</div>
              </div>
              <button onClick={() => remove(it.id)} className="p-1 rounded hover:bg-accent text-destructive shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete interaction" data-testid={`task-interaction-delete-${it.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CardShell({ icon: Icon, title, count, onAdd, addTestid, anchorId, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={anchorId} className="rounded-lg border border-border bg-card overflow-hidden scroll-mt-24">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-secondary/30">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-semibold flex-1 min-w-0 text-left"
          data-testid={`${anchorId}-toggle`}
        >
          <Icon className="h-4 w-4 text-primary shrink-0" />
          {title}
          <span className="text-xs font-normal text-muted-foreground">({count})</span>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={addTestid} className="h-7 px-2.5 shrink-0">
          <Plus className="h-3.5 w-3.5 mr-1" />New
        </Button>
      </div>
      {open && children}
    </div>
  );
}

export function TasksCard({ recordId, resource, onChange, listSignal, reloadToken }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [form, setForm] = useState(BLANK_TASK);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/tasks?record_id=${recordId}&limit=100&sort_by=created_at&sort_dir=desc`)
      .then((r) => setTasks(r.data.items || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [recordId]);
  useEffect(() => { if (reloadToken) load(); }, [reloadToken]);
  useEffect(() => { if (listSignal) setListOpen(true); }, [listSignal]);

  const openNew = () => { setEditing(null); setForm(BLANK_TASK); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || "", description: t.description || "", task_type: t.task_type || "Follow-Up",
      priority: t.priority || "Medium", due_date: (t.due_date || "").slice(0, 10), status: t.status || "To Do",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/tasks/${editing.id}`, form);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", { ...form, record_id: recordId, resource, lead_id: recordId });
        toast.success("Task added");
      }
      setOpen(false); setEditing(null); setForm(BLANK_TASK);
      load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const completeAndSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, status: "Completed" };
      if (editing) {
        await api.put(`/tasks/${editing.id}`, payload);
      } else {
        await api.post("/tasks", { ...payload, record_id: recordId, resource, lead_id: recordId });
      }
      toast.success("Task marked complete");
      setOpen(false); setEditing(null); setForm(BLANK_TASK);
      load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (t) => {
    const next = t.status === "Completed" ? "To Do" : "Completed";
    try {
      await api.put(`/tasks/${t.id}`, { status: next });
      setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
      onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const markComplete = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: "Completed" });
      setTasks((prev) => prev.map((x) => (x.id === taskId ? { ...x, status: "Completed" } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    if (!editing) return;
    try {
      await api.delete(`/tasks/${editing.id}?hard=true`);
      toast.success("Task deleted");
      setConfirmDel(false); setOpen(false); setEditing(null);
      load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rows = (onRowClick) => (
    <ul className="divide-y divide-border/60">
      {tasks.map((t) => {
        const isOpen = !!expanded[t.id];
        return (
          <li key={t.id} className="px-5 py-3 hover:bg-accent/40 transition-colors" data-testid={`task-row-${t.id}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => toggle(t)} className="mt-0.5 text-muted-foreground hover:text-primary shrink-0" data-testid={`task-toggle-${t.id}`} aria-label="Toggle complete">
                {t.status === "Completed" ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <button onClick={() => onRowClick(t)} className="min-w-0 w-full text-left" data-testid={`task-open-${t.id}`}>
                  <div className={`text-sm font-medium leading-snug ${isOpen ? "" : "line-clamp-1"} ${t.status === "Completed" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                </button>
                {isOpen && (
                  <div className="mt-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.task_type && <StatusBadge value={t.task_type} />}
                      {t.priority && <StatusBadge value={t.priority} />}
                      {t.due_date && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarClock className="h-3 w-3" />{formatDate(t.due_date)}
                        </span>
                      )}
                    </div>
                    {t.description && <div className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap">{t.description}</div>}
                    <TaskAttachments taskId={t.id} recordId={recordId} resource={resource} task={t} onAutoComplete={markComplete} onChange={onChange} />
                  </div>
                )}
              </div>
              <button onClick={() => setExpanded((e) => ({ ...e, [t.id]: !e[t.id] }))} className="mt-0.5 text-muted-foreground hover:text-primary shrink-0" data-testid={`task-expand-${t.id}`} aria-label={isOpen ? "Collapse task" : "Expand task"}>
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );

  const emptyState = <div className="px-5 py-6 text-center text-sm text-muted-foreground">No tasks yet. Log a follow-up to stay on track.</div>;

  return (
    <CardShell icon={CheckSquare} title="Tasks" count={tasks.length} onAdd={openNew} addTestid="new-task-btn" anchorId="record-tasks-card" defaultOpen={false}>
      <div data-testid="tasks-card-body">
        {loading ? <div className="px-5 py-5 text-sm text-muted-foreground">Loading…</div> : tasks.length === 0 ? emptyState : rows(openEdit)}
      </div>

      {/* List view (opened from Quick Links) */}
      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="max-w-lg" data-testid="tasks-list-dialog">
          <DialogHeader>
            <DialogTitle>Tasks ({tasks.length})</DialogTitle>
            <DialogDescription>Select a task to manage it, or add a new one.</DialogDescription>
          </DialogHeader>
          <div className="-mx-6 max-h-[60vh] overflow-y-auto border-y border-border">
            {tasks.length === 0 ? emptyState : rows((t) => { setListOpen(false); openEdit(t); })}
          </div>
          <DialogFooter>
            <Button onClick={() => { setListOpen(false); openNew(); }} data-testid="tasks-list-new-btn">
              <Plus className="h-4 w-4 mr-1.5" />New Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-page task editor (replaces the cramped modal for more room) */}
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background fade-up" data-testid="task-dialog">
          <div className="flex items-center gap-3 border-b border-border px-4 sm:px-6 py-4 shrink-0">
            <button onClick={() => setOpen(false)} className="p-2 -ml-2 rounded-md hover:bg-accent text-muted-foreground transition-colors" data-testid="task-back-btn" aria-label="Back">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold leading-tight tracking-tight">{editing ? "Manage task" : "New task"}</h2>
              <p className="text-xs text-muted-foreground truncate">{editing ? "Update or delete this task." : "Log a task to track follow-up on this record."}</p>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto p-2 -mr-2 rounded-md hover:bg-accent text-muted-foreground transition-colors" data-testid="task-close-btn" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto slim-scroll">
            <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8 space-y-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">Title</div>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Follow up on estimate" data-testid="task-title-input" autoFocus />
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">Description</div>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="min-h-[140px] resize-y" placeholder="Add details, context, or research notes…" data-testid="task-description-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">Type</div>
                  <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                    <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">Priority</div>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_PRIORITIES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">Due date</div>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">Status</div>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_STATUSES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {editing && (
                <TaskInteractions taskId={editing.id} />
              )}

              {editing && (
                <TaskAttachments taskId={editing.id} recordId={recordId} resource={resource} task={editing} onAutoComplete={markComplete} onChange={onChange} />
              )}
            </div>
          </div>

          <div className="border-t border-border px-4 sm:px-6 py-4 shrink-0 bg-background">
            <div className="mx-auto w-full max-w-3xl flex items-center justify-between gap-2">
              {editing ? (
                <Button variant="ghost" onClick={() => setConfirmDel(true)} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="task-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" />Delete
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="task-cancel-btn">Cancel</Button>
                {form.status !== "Completed" && (
                  <Button variant="outline" onClick={completeAndSave} disabled={saving} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300" data-testid="task-complete-btn">
                    <CheckSquare className="h-4 w-4 mr-1.5" />Mark Complete
                  </Button>
                )}
                <Button onClick={submit} disabled={saving} data-testid="task-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}{editing ? "Save" : "Add Task"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid="task-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this task. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="task-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="task-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CardShell>
  );
}

export function NotesCard({ recordId, resource, onChange, listSignal }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [body, setBody] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/notes?record_id=${recordId}&limit=100&sort_by=created_at&sort_dir=desc`)
      .then((r) => setNotes(r.data.items || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [recordId]);
  useEffect(() => { if (listSignal) setListOpen(true); }, [listSignal]);

  const openNew = () => { setEditing(null); setBody(""); setOpen(true); };
  const openEdit = (n) => { setEditing(n); setBody(n.body || ""); setOpen(true); };

  const toggle = async (n) => {
    const next = !n.done;
    try {
      await api.put(`/notes/${n.id}`, { done: next });
      setNotes((prev) => prev.map((x) => (x.id === n.id ? { ...x, done: next } : x)));
      onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    if (!body.trim()) { toast.error("Note cannot be empty"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/notes/${editing.id}`, { body });
        toast.success("Note updated");
      } else {
        await api.post("/notes", { body, record_id: recordId, resource, lead_id: recordId, author_name: user?.name });
        toast.success("Note added");
      }
      setOpen(false); setEditing(null); setBody("");
      load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!editing) return;
    try {
      await api.delete(`/notes/${editing.id}?hard=true`);
      toast.success("Note deleted");
      setConfirmDel(false); setOpen(false); setEditing(null);
      load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rows = (onRowClick) => (
    <ul className="divide-y divide-border/60">
      {notes.map((n) => {
        const isOpen = !!expanded[n.id];
        return (
          <li key={n.id} className="px-5 py-3 hover:bg-accent/40 transition-colors" data-testid={`note-row-${n.id}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => toggle(n)} className="mt-0.5 text-muted-foreground hover:text-primary shrink-0" data-testid={`note-toggle-${n.id}`} aria-label="Mark note done">
                {n.done ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <button onClick={() => onRowClick(n)} className="min-w-0 w-full text-left" data-testid={`note-open-${n.id}`}>
                  <div className={`text-sm leading-snug ${isOpen ? "whitespace-pre-wrap" : "line-clamp-2"} ${n.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{n.body}</div>
                </button>
                <div className="text-xs text-muted-foreground mt-1.5">{n.author_name || "Someone"} · {formatDateTime(n.created_at)}</div>
              </div>
              <button onClick={() => setExpanded((e) => ({ ...e, [n.id]: !e[n.id] }))} className="mt-0.5 text-muted-foreground hover:text-primary shrink-0" data-testid={`note-expand-${n.id}`} aria-label={isOpen ? "Collapse note" : "Expand note"}>
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );

  const emptyState = <div className="px-5 py-6 text-center text-sm text-muted-foreground">No notes yet. Capture context for your team.</div>;

  return (
    <CardShell icon={StickyNote} title="Notes" count={notes.length} onAdd={openNew} addTestid="new-note-btn" anchorId="record-notes-card" defaultOpen={false}>
      <div data-testid="notes-card-body">
        {loading ? <div className="px-5 py-5 text-sm text-muted-foreground">Loading…</div> : notes.length === 0 ? emptyState : rows(openEdit)}
      </div>

      {/* List view (opened from Quick Links) */}
      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="max-w-lg" data-testid="notes-list-dialog">
          <DialogHeader>
            <DialogTitle>Notes ({notes.length})</DialogTitle>
            <DialogDescription>Select a note to manage it, or add a new one.</DialogDescription>
          </DialogHeader>
          <div className="-mx-6 max-h-[60vh] overflow-y-auto border-y border-border">
            {notes.length === 0 ? emptyState : rows((n) => { setListOpen(false); openEdit(n); })}
          </div>
          <DialogFooter>
            <Button onClick={() => { setListOpen(false); openNew(); }} data-testid="notes-list-new-btn">
              <Plus className="h-4 w-4 mr-1.5" />New Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Manage note" : "New note"}</DialogTitle>
            <DialogDescription>{editing ? "Update or delete this note." : "Add a note to this record's history."}</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Write a note…" data-testid="note-body-input" autoFocus />
          </div>
          <DialogFooter className="sm:justify-between">
            {editing ? (
              <Button variant="ghost" onClick={() => setConfirmDel(true)} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="note-delete-btn">
                <Trash2 className="h-4 w-4 mr-1.5" />Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="note-cancel-btn">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="note-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}{editing ? "Save" : "Add Note"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid="note-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this note. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="note-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="note-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CardShell>
  );
}
